import secureApiService from './secureApiService'
import { LicenseModel, LicenseStatus } from '@/models/license.model'
import { PaginatedResponse, PaginationMeta, normalizePaginationMeta } from '@/models/templates/pagination.model'

interface LicenseJSON {
  [key: string]: any
}

interface LicensesResponseJSON {
  success: boolean
  message?: string
  data?: LicenseJSON[] | LicenseJSON | null
  meta?: Record<string, any> | null
}

interface LicenseResponseJSON {
  success: boolean
  message?: string
  data?: LicenseJSON | { license?: LicenseJSON } | null
}

interface LicenseWithCertificateResponseJSON {
  success: boolean
  message?: string
  data?: {
    license?: LicenseJSON | null
    [key: string]: any
  } | null
}

interface WithdrawResponseJSON {
  success: boolean
  message?: string
  data?: {
    licenseId?: string
    revokedCertificatesCount?: number
  } | null
}

export interface LicenseListParams {
  page?: number
  perPage?: number
  userId?: string
  type?: string
  programmeId?: string
  status?: LicenseStatus | string
}

export interface RecordExamPayload {
  examScore: number
  examTakenAt?: string
}

export interface ScheduleInterviewPayload {
  scheduledAt: string
  zoomLink?: string
}

export interface RecordInterviewResultPayload {
  score: number
  passed: boolean
}

export interface IssueLicensePayload {
  score?: number
  passed?: boolean
}

export interface WithdrawLicensePayload {
  reason: string
  revokeCertificate?: boolean
}

class LicensesService {
  private basePath = '/licenses'

  async getLicenses(params: LicenseListParams = {}): Promise<PaginatedResponse<LicenseModel>> {
    const searchParams = new URLSearchParams()
    if (params.page) searchParams.set('page', String(params.page))
    if (params.perPage) searchParams.set('perPage', String(params.perPage))
    if (params.userId) searchParams.set('userId', params.userId)
    if (params.type) searchParams.set('type', params.type)
    if (params.programmeId) searchParams.set('programmeId', params.programmeId)
    if (params.status) searchParams.set('status', String(params.status))

    const endpoint = searchParams.toString() ? `${this.basePath}?${searchParams.toString()}` : this.basePath

    const response = await secureApiService.get<LicensesResponseJSON>(endpoint, {
      requireAuth: true,
    })

    const payload = response.data
    if (!payload?.success) {
      throw new Error(payload?.message || 'Unable to fetch licenses')
    }

    const items = Array.isArray(payload.data)
      ? payload.data
      : payload.data
        ? [payload.data]
        : []

    const data = items.map((item) => LicenseModel.fromJSON(item))

    const meta: PaginationMeta = normalizePaginationMeta(payload.meta) ?? {
      total: data.length,
      page: params.page ?? 1,
      perPage: params.perPage ?? (data.length || 25),
    }

    return { data, meta }
  }

  async getLicenseById(licenseId: string): Promise<LicenseModel> {
    if (!licenseId) {
      throw new Error('License ID is required')
    }

    const response = await secureApiService.get<LicenseResponseJSON>(`${this.basePath}/${licenseId}`, {
      requireAuth: true,
    })

    const payload = response.data
    const data = payload?.data && 'license' in payload.data
      ? payload.data.license
      : payload?.data

    if (!payload?.success || !data) {
      throw new Error(payload?.message || 'Unable to fetch license')
    }

    return LicenseModel.fromJSON(data)
  }

  async recordExamResult(userId: string, payload: RecordExamPayload): Promise<LicenseModel> {
    if (!userId) {
      throw new Error('User ID is required')
    }

    const response = await secureApiService.post<LicenseResponseJSON>(`${this.basePath}/${userId}/exam`, payload, {
      requireAuth: true,
    })

    const data = response.data?.data
    if (!response.data?.success || !data) {
      throw new Error(response.data?.message || 'Unable to record exam result')
    }

    return LicenseModel.fromJSON(data)
  }

  async scheduleInterview(licenseId: string, payload: ScheduleInterviewPayload): Promise<LicenseModel> {
    if (!licenseId) {
      throw new Error('License ID is required')
    }

    const response = await secureApiService.post<LicenseResponseJSON>(`${this.basePath}/${licenseId}/schedule-interview`, payload, {
      requireAuth: true,
    })

    const data = response.data?.data
    if (!response.data?.success || !data) {
      throw new Error(response.data?.message || 'Unable to schedule interview')
    }

    return LicenseModel.fromJSON(data)
  }

  async recordInterviewResult(licenseId: string, payload: RecordInterviewResultPayload): Promise<LicenseModel> {
    if (!licenseId) {
      throw new Error('License ID is required')
    }

    const response = await secureApiService.post<LicenseWithCertificateResponseJSON>(`${this.basePath}/${licenseId}/interview-result`, payload, {
      requireAuth: true,
    })

    const rawData = response.data?.data
    const data =
      rawData && typeof rawData === 'object' && 'license' in rawData
        ? (rawData as { license?: LicenseJSON | null }).license
        : rawData

    if (!response.data?.success || !data) {
      throw new Error(response.data?.message || 'Unable to record interview result')
    }

    return LicenseModel.fromJSON(data)
  }

  async issueLicense(licenseId: string, payload: IssueLicensePayload = {}): Promise<LicenseModel> {
    if (!licenseId) {
      throw new Error('License ID is required')
    }

    const response = await secureApiService.post<LicenseWithCertificateResponseJSON>(`${this.basePath}/${licenseId}/issue`, payload, {
      requireAuth: true,
    })

    const data = response.data?.data?.license
    if (!response.data?.success || !data) {
      throw new Error(response.data?.message || 'Unable to issue license')
    }

    return LicenseModel.fromJSON(data)
  }

  async withdrawLicense(licenseId: string, payload: WithdrawLicensePayload) {
    if (!licenseId) {
      throw new Error('License ID is required')
    }

    const response = await secureApiService.post<WithdrawResponseJSON>(`${this.basePath}/${licenseId}/withdraw`, payload, {
      requireAuth: true,
    })

    const data = response.data?.data
    if (!response.data?.success || !data) {
      throw new Error(response.data?.message || 'Unable to withdraw license')
    }

    return data
  }
}

export const licensesService = new LicensesService()
